import React, { useContext, useEffect, useState } from 'react';
import { PAContext } from "../../../Services/PAContext";
import {
    Box, CardContent, Checkbox, Chip, Menu, MenuItem, Tooltip,
    Typography, IconButton, ListItemIcon, ListItemText, Divider, Stack
} from "@mui/material";
import dayjs from "dayjs";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";

// Dialog Imports
import TodoDialogEditTitle from "./TodoDialogEditTitle";
import TodoDialogEditNotes from "./TodoDialogEditNotes";
import TodoDialogEditCategory from "./TodoDialogEditCategory";
import TodoDialogEditPriority from "./TodoDialogEditPriority";
import TodoDialogEditSchedule from "./TodoDialogEditSchedule";
import TodoDialogEditReminders from "./TodoDialogEditReminders";
import TodoDialogEditPrivate from "./TodoDialogEditPrivate";

function TodoCard(props) {
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Attendees, SampleAttendees, authenticated } = dataArray;

    const [attendeesReferenceArray, setAttendeesReferenceArray] = useState(
        authenticated ? Attendees : SampleAttendees
    );

    const [eventTodo, setEventTodo] = useState(props.to_do);
    const [anchorTodoListItemMenu, setAnchorTodoListItemMenu] = useState(null);
    const [activeDialog, setActiveDialog] = useState(null);

    // --- Sync Logic ---
    useEffect(() => {
        setAttendeesReferenceArray(authenticated ? Attendees : SampleAttendees);
    }, [authenticated, Attendees, SampleAttendees]);

    function deepEqual(a, b) {
        if (a === b) return true;
        if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
        const aKeys = Object.keys(a), bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const k of aKeys) if (!deepEqual(a[k], b[k])) return false;
        return true;
    }

    function deepMergePreserveExtras(base, patch) {
        if (patch === null) return null;
        const baseIsObj = base && typeof base === "object";
        const patchIsObj = patch && typeof patch === "object";
        if (!baseIsObj || !patchIsObj) return patch;
        if (Array.isArray(base) || Array.isArray(patch)) return Array.isArray(patch) ? patch.slice() : patch;
        const out = { ...base };
        for (const key of Object.keys(patch)) {
            const merged = deepMergePreserveExtras(base[key], patch[key]);
            merged === null ? delete out[key] : out[key] = merged;
        }
        return out;
    }

    useEffect(() => {
        const merged = deepMergePreserveExtras(eventTodo, props.to_do);
        if (!deepEqual(merged, eventTodo)) setEventTodo(merged);
    }, [props.to_do]);

    useEffect(() => {
        set_PA_GlobalObject((prev) => {
            const updatedTodos = prev.Planning.to_do.map((_to_do) =>
                _to_do.id === eventTodo.id ? { ..._to_do, ...eventTodo } : _to_do
            );
            return { Planning: { ...prev.Planning, to_do: updatedTodos } };
        });

        if (PA_GlobalObject.Planning.status !== 10) {
            sendSocketMessage({
                action: "E_updateEventTodoItem_v2",
                event_id: PA_GlobalObject.Planning.id,
                todo: eventTodo
            });
        }
    }, [eventTodo]);

    // --- UI Helpers ---
    const handleCheckboxChange = (event) => {
        setEventTodo({ ...eventTodo, status: event.target.checked ? 0 : 1 });
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "Low": return "#22c55e";
            case "Medium": return "#f59e0b";
            case "High": return "#f97316";
            case "Urgent": return "#ef4444";
            default: return "#64748b";
        }
    };

    const priorityColor = getPriorityColor(eventTodo.priority);

    const handleTodoListItemMenuOpenMenu = (event) => setAnchorTodoListItemMenu(event.currentTarget);
    const handleTodoListItemMenuClose = () => setAnchorTodoListItemMenu(null);

    const handleFooterItemClick = (_item) => {
        handleTodoListItemMenuClose();
        if (_item === 'Delete') props.onDeleteTodo();
        else setActiveDialog(_item);
    };

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    mb: 2,
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        borderColor: '#cbd5e1'
                    }
                }}
            >
                {/* Priority Indicator Strip */}
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: '5px',
                    background: priorityColor,
                    boxShadow: `2px 0 8px ${priorityColor}33`
                }} />

                <CardContent sx={{ p: '16px !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 800,
                                color: eventTodo.status === 0 ? '#94a3b8' : '#1e293b',
                                textDecoration: eventTodo.status === 0 ? 'line-through' : 'none',
                                transition: '0.2s',
                                mb: 0.5
                            }}>
                                {eventTodo.title}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: '6px' }}>
                                    <i className="icon-calendar" style={{ fontSize: '12px', color: '#64748b' }} />
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#475569' }}>
                                        {dayjs(eventTodo.schedule_date).format('ddd, MMM D')}
                                    </Typography>
                                </Box>
                                {eventTodo.is_private && (
                                    <Chip
                                        label="PRIVATE"
                                        size="small"
                                        sx={{ height: '18px', fontSize: '9px', fontWeight: 900, bgcolor: '#fee2e2', color: '#b91c1c' }}
                                    />
                                )}
                            </Stack>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            <Checkbox
                                checked={eventTodo.status === 0}
                                onChange={handleCheckboxChange}
                                sx={{ p: 0.5, color: '#cbd5e1', '&.Mui-checked': { color: '#10b981' } }}
                            />
                            <IconButton onClick={handleTodoListItemMenuOpenMenu} size="small" sx={{ ml: 0.5 }}>
                                <i className="icon-menu2" style={{ fontSize: '16px', color: '#94a3b8' }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* NOTES SECTION */}
                    {eventTodo.notes && (
                        <Typography sx={{
                            fontSize: '13px', color: '#64748b', fontWeight: 500, mt: 1.5,
                            bgcolor: '#f8fafc', p: 1.5, borderRadius: '10px', border: '1px solid #f1f5f9'
                        }}>
                            {eventTodo.notes}
                        </Typography>
                    )}

                    {/* REMINDERS TRAY */}
                    {(eventTodo["reminder_days_before_array"]?.length > 0 || eventTodo["reminder_day_of_time"]) && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <i className="icon-notification" style={{ color: '#f97316', fontSize: '14px' }} />
                                <Typography sx={{ fontSize: '11px', fontWeight: 900, color: '#f97316', letterSpacing: 0.5 }}>
                                    UPCOMING REMINDERS
                                </Typography>
                            </Box>

                            <Stack spacing={1}>
                                {/* Day(s) Before Reminders */}
                                {eventTodo["reminder_days_before_array"]?.sort((a, b) => b - a).map((day) => (
                                    <Box key={`rem-${day}`} sx={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        bgcolor: '#fffbf5', px: 1.5, py: 0.8, borderRadius: '8px', border: '1px solid #ffedd5'
                                    }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#9a3412' }}>
                                            {day} Day{day > 1 ? 's' : ''} Before
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#ea580c' }}>
                                            {eventTodo["reminder_days_before_time"] || "No Time Set"}
                                        </Typography>
                                    </Box>
                                ))}

                                {/* Day Of Reminder */}
                                {eventTodo["reminder_day_of_time"] && (
                                    <Box sx={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        bgcolor: '#f0fdf4', px: 1.5, py: 0.8, borderRadius: '8px', border: '1px solid #dcfce7'
                                    }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#166534' }}>
                                            Day of Event
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#15803d' }}>
                                            {eventTodo["reminder_day_of_time"]}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* FOOTER INFO */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2.5,
                        pt: 1.5,
                        borderTop: '1px solid #f1f5f9'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{
                                width: 28, height: 28, bgcolor: '#f1f5f9',
                                borderRadius: '8px', display: 'flex',
                                alignItems: 'center', justifyContent: 'center'
                            }}>
                                <i className="icon-alarm" style={{ fontSize: '14px', color: '#64748b' }} />
                            </Box>
                            <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: 0.3 }}>
                                {eventTodo.schedule_all_day ? (
                                    'ALL DAY'
                                ) : (
                                    eventTodo.schedule_begin_time_selected && eventTodo.schedule_end_time_selected ? (
                                        `${eventTodo.schedule_begin_time} — ${eventTodo.schedule_end_time}`
                                    ) : eventTodo.schedule_begin_time_selected ? (
                                        `FROM ${eventTodo.schedule_begin_time}`
                                    ) : eventTodo.schedule_end_time_selected ? (
                                        `BY ${eventTodo.schedule_end_time}`
                                    ) : (
                                        'NO TIME SET'
                                    )
                                )}
                            </Typography>
                        </Box>

                        {eventTodo.assigned_to !== eventTodo.created_by && (
                            <Tooltip title="Task Assignee">
                                <Chip
                                    label={`FOR: ${capitalizeFirstLetter(attendeesReferenceArray.find(a => a.id === eventTodo.assigned_to)?.nickname || 'ME')}`}
                                    sx={{
                                        height: '22px',
                                        fontSize: '9px',
                                        fontWeight: 900,
                                        bgcolor: '#e0e7ff',
                                        color: '#4338ca',
                                        border: '1px solid #c7d2fe'
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </CardContent>
            </Box>

            {/* Premium Action Menu */}
            <Menu
                anchorEl={anchorTodoListItemMenu}
                open={Boolean(anchorTodoListItemMenu)}
                onClose={handleTodoListItemMenuClose}
                PaperProps={{ sx: { borderRadius: '14px', width: 240, mt: 1, p: 0.5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } }}
            >
                <Box sx={{ px: 2, py: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#94a3b8', letterSpacing: 1 }}>SETTINGS</Typography>
                </Box>
                {[
                    { id: 'Title', icon: 'icon-pencil', color: '#6366f1', label: 'Rename Task' },
                    { id: 'Notes', icon: 'icon-file-text', color: '#8b5cf6', label: 'Edit Notes' },
                    { id: 'Category', icon: 'icon-tag', color: '#ec4899', label: 'Category' },
                    { id: 'Priority', icon: 'icon-flag', color: '#ef4444', label: 'Set Priority' },
                    { id: 'Schedule', icon: 'icon-calendar', color: '#10b981', label: 'Schedule' },
                    { id: 'Reminder', icon: 'icon-alarm', color: '#f59e0b', label: 'Reminders' },
                    { id: 'Private', icon: 'icon-lock', color: '#64748b', label: 'Privacy' },
                ].map((item) => (
                    <MenuItem key={item.id} onClick={() => handleFooterItemClick(item.id)} sx={{ borderRadius: '8px', mb: 0.2 }}>
                        <ListItemIcon>
                            <Box sx={{ width: 30, height: 30, bgcolor: `${item.color}15`, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className={item.icon} style={{ color: item.color, fontSize: '14px' }} />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                    </MenuItem>
                ))}
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={() => handleFooterItemClick('Delete')} sx={{ borderRadius: '8px', color: '#ef4444' }}>
                    <ListItemIcon>
                        <Box sx={{ width: 30, height: 30, bgcolor: '#fee2e2', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="icon-trash" style={{ color: '#ef4444', fontSize: '14px' }} />
                        </Box>
                    </ListItemIcon>
                    <ListItemText primary="Delete" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                </MenuItem>
            </Menu>

            {/* Dialogs */}
            <TodoDialogEditTitle isOpen={activeDialog === 'Title'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditNotes isOpen={activeDialog === 'Notes'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditCategory isOpen={activeDialog === 'Category'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditPriority isOpen={activeDialog === 'Priority'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditSchedule isOpen={activeDialog === 'Schedule'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditReminders isOpen={activeDialog === 'Reminder'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
            <TodoDialogEditPrivate isOpen={activeDialog === 'Private'} onClose={() => setActiveDialog(null)} eventTodo={eventTodo} setEventTodo={setEventTodo} />
        </>
    );
}

export default TodoCard;